import { createContext, useContext, useEffect, useReducer } from 'react'
import type { ReactNode } from 'react'

import { writeSessionHint } from '@/lib/auth/sessionHint'

interface BadclawTenant {
  tenantId: string
  containerPort: number
  subdomain: string
  provisionedAt: string
}

export interface DashboardState {
  user: { name: string; email: string; hasSetPassword?: boolean; badclawTenant?: BadclawTenant } | null
  isAuthenticated: boolean
  isLoading: boolean
  theme: 'light' | 'dark'
  isSidebarCollapsed: boolean
  /**
   * This tab had a session and lost it: signed out here or in another tab, or expired. Its login page then
   * carries no return path, so the next person to sign in on the browser doesn't land on the last person's page.
   */
  sessionEnded: boolean
}

type DashboardAction =
  | { type: 'SET_USER'; payload: DashboardState['user'] }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_THEME'; payload: 'light' | 'dark' }
  | { type: 'TOGGLE_SIDEBAR' }
  | { type: 'LOGOUT' }

export interface ChatMessage {
  role: 'user' | 'assistant' | 'system'
  content: string
  images?: string[]
}

function getInitialTheme(): 'light' | 'dark' {
  const saved = localStorage.getItem('badland-theme')
  return saved === 'light' ? 'light' : 'dark'
}

const initialState: DashboardState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  theme: getInitialTheme(),
  isSidebarCollapsed: false,
  sessionEnded: false,
}

function reducer(state: DashboardState, action: DashboardAction): DashboardState {
  switch (action.type) {
    case 'SET_USER':
      // A visitor who arrives signed out never had a session here, so their deep link survives sign-in.
      return {
        ...state,
        user: action.payload,
        isAuthenticated: !!action.payload,
        isLoading: false,
        sessionEnded: action.payload ? false : state.sessionEnded || state.user !== null,
      }
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload }
    case 'SET_THEME':
      return { ...state, theme: action.payload }
    case 'TOGGLE_SIDEBAR':
      return { ...state, isSidebarCollapsed: !state.isSidebarCollapsed }
    case 'LOGOUT':
      return { ...initialState, isLoading: false, sessionEnded: true }
    default:
      return state
  }
}

const DashboardContext = createContext<{
  state: DashboardState
  dispatch: React.Dispatch<DashboardAction>
} | null>(null)

export function DashboardProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState)

  useEffect(() => {
    document.documentElement.classList.toggle('dark', state.theme === 'dark')
    localStorage.setItem('badland-theme', state.theme)
    // Match the mobile status bar to this theme's surface token.
    const surface = getComputedStyle(document.documentElement).getPropertyValue('--color-surface-primary').trim()
    if (surface) document.querySelector('meta[name="theme-color"]')?.setAttribute('content', `rgb(${surface})`)
  }, [state.theme])

  // The next visit's first paint (index.html): the sign-in gate, or the camp's loader.
  useEffect(() => {
    if (!state.isLoading) writeSessionHint(state.isAuthenticated)
  }, [state.isLoading, state.isAuthenticated])

  return (
    <DashboardContext.Provider value={{ state, dispatch }}>
      {children}
    </DashboardContext.Provider>
  )
}

export function useDashboard() {
  const ctx = useContext(DashboardContext)
  if (!ctx) throw new Error('useDashboard must be used within DashboardProvider')
  return ctx
}
